import { Editor, Transforms, Element } from 'slate';
import { flattenMdast, isNonEmptyContent, isEmptyLikeContent, } from './flatten-mdast';
import { createBlockTexts, mapLine } from './block-texts';
import { parse } from './parse';
import { isFencedCodeBlock } from './utils';
import { isTopLevel, isEmptyElement, } from '..';
const isTopLevelSibling = (maybeSiblingElement, selfType, selfAncestors, prev) => {
    if (!Element.isElement(maybeSiblingElement))
        return false;
    if (!isTopLevel(maybeSiblingElement))
        return false;
    const maybeSiblingType = maybeSiblingElement.type;
    const maybeSiblingAncestor = maybeSiblingElement.flattenedMdastAncestors;
    if (maybeSiblingAncestor == null) {
        return false;
    }
    if (maybeSiblingType === 'empty') {
        return false;
    }
    // NOTE: Special case like the following
    // * line0
    //     * line1  // This line is parsed as `code` if parsed as a single line, but actually is a part of a paragraph.
    if (
    // prev &&
    (selfType === 'paragraph' || selfType === 'code') &&
        selfAncestors.length <= 1 && // 'root' is the only ancestor
        maybeSiblingAncestor[1] === 'list') {
        return true;
    }
    // NOTE: Special case like the following
    // *
    // a
    // ---
    // When the first line is parsed alone, it is an element under a list.
    // It should be jointed with the second line, which can be parsed alone as a paragraph
    if (!prev &&
        selfAncestors[1] === 'list' &&
        (maybeSiblingType === 'paragraph' || maybeSiblingType === 'code') &&
        maybeSiblingAncestor.length <= 2) {
        return true;
    }
    // NOTE: If a next sibling is a list item with 1-level depth,
    // it is unnecessary to combine it with a self element to parse.
    // * foo <- self
    // * bar
    // ---
    //   * foo <- self
    // * bar
    if (!prev &&
        maybeSiblingAncestor.length === 3 &&
        maybeSiblingAncestor[2] === 'listItem') {
        return false;
    }
    if (selfAncestors.length <= 1 && maybeSiblingAncestor.length <= 1) {
        if (selfType === maybeSiblingType) {
            return true;
        }
        // NOTE: Exceptional case like following
        // line0
        //     line1  // This line is parsed as `code` if parsed as a single line, but actually is a part of a paragraph.
        if ((selfType === 'paragraph' && maybeSiblingType === 'code') ||
            (selfType === 'code' && maybeSiblingType === 'paragraph')) {
            return true;
        }
        return false;
    }
    // ancestors[0] must be 'root'
    return selfAncestors[1] === maybeSiblingAncestor[1];
};
// Checks if ancestors2 is a subset of ancestors1
const isSubset = (ancestors1, ancestors2) => {
    if (ancestors2.length > ancestors1.length) {
        return false;
    }
    return ancestors2.every((v, i) => v === ancestors1[i]);
};
export const getToplevelSiblingElements = (selfContent, selfAncestors, selfIndex, editor, prev) => {
    const maybeSibElements = prev
        ? editor.children.slice(0, selfIndex).reverse()
        : editor.children.slice(selfIndex + 1);
    const selfIsFencedCodeBlock = selfContent.type === 'code' && isFencedCodeBlock(selfContent);
    let prevSibType = selfContent.type;
    let prevSibAncestors = selfAncestors;
    let i;
    for (i = 0; i < maybeSibElements.length; ++i) {
        const targetElement = maybeSibElements[i];
        const targetAncestors = targetElement.flattenedMdastAncestors || ['root'];
        if ((selfIsFencedCodeBlock && isSubset(targetAncestors, selfAncestors)) ||
            isTopLevelSibling(targetElement, prevSibType, prevSibAncestors, prev)) {
            prevSibType = targetElement.type;
            prevSibAncestors = targetElement.flattenedMdastAncestors;
        }
        else {
            break;
        }
    }
    const sibElements = maybeSibElements.slice(0, i);
    if (prev) {
        sibElements.reverse();
    }
    return sibElements;
};
const getUntilNonEmtpyElements = (editor, selfIndex, prev) => {
    const searchTargets = prev
        ? editor.children.slice(0, selfIndex).reverse()
        : editor.children.slice(selfIndex + 1);
    let found = false;
    let prevSibType = 'empty';
    let prevSibAncestors;
    let i;
    for (i = 0; i < searchTargets.length; ++i) {
        const targetElement = searchTargets[i];
        if (found) {
            if (!prevSibAncestors ||
                !isTopLevelSibling(targetElement, prevSibType, prevSibAncestors, true)) {
                break;
            }
        }
        else {
            if (!isEmptyElement(targetElement)) {
                found = true;
                prevSibType = targetElement.type;
                prevSibAncestors = targetElement.flattenedMdastAncestors;
            }
        }
    }
    const family = found ? searchTargets.slice(0, i) : [];
    if (prev) {
        family.reverse();
    }
    return [found, family];
};
export const countLines = (topLevelBlocks) => {
    return topLevelBlocks.reduce((acc, element) => {
        const linesInThisElement = element.children.filter((child) => child.type === 'line').length;
        return acc + linesInThisElement;
    }, 0);
};
const topLevelElementFields = [
    'type',
    'children',
    'flattenedMdastPath',
    'flattenedMdastAncestors',
    'mdastNode',
    'parentMdastNode',
];
/**
 * Remove additional fields from the TopLevelElement in given `editor` specified by `index`.
 */
const sanitizeTopLevelElement = (editor, index) => {
    const targetElement = editor.children[index];
    const unsetFields = [];
    for (const field of Object.keys(targetElement)) {
        if (!topLevelElementFields.includes(field)) {
            unsetFields.push(field);
        }
    }
    Transforms.unsetNodes(editor, unsetFields, { at: [index] });
};
const unsetMdastPositions = (mdastNode) => {
    return {
        ...mdastNode,
        children: mdastNode.children &&
            mdastNode.children.map(unsetMdastPositions),
        position: undefined,
    };
};
export const checkTopLevelElementChanged = (original, nextProps) => {
    if (original.type !== nextProps.type)
        return 'content-type-changed';
    if (JSON.stringify(original.flattenedMdastAncestors) !==
        JSON.stringify(nextProps.flattenedMdastAncestors)) {
        return 'mdast-ancestors-changed';
    }
    // TODO: Replace with shallow comparing with memoizing parse()
    // NOTE: *.position is not reliable in tasquet's normalization procedure.
    const originalMdastComp = original.mdastNode && unsetMdastPositions(original.mdastNode);
    const nextPropsMdastComp = nextProps.mdastNode && unsetMdastPositions(nextProps.mdastNode);
    if (JSON.stringify(originalMdastComp) !== JSON.stringify(nextPropsMdastComp)) {
        return 'mdast-node-changed';
    }
    // NOTE: parent.children and position are not reliable in tasquet's normalization procedure.
    const originalParentComp = {
        ...original.parentMdastNode,
        children: null,
        position: null,
    };
    const nextPropsParentComp = {
        ...nextProps.parentMdastNode,
        children: null,
        position: null,
    };
    if (JSON.stringify(originalParentComp) !== JSON.stringify(nextPropsParentComp)) {
        return 'parent-mdast-node-changed';
    }
    return 'unchanged';
};
const callListeners = (listeners, currentMdast, newMdast, parsedText, topLevelElementIndex, oldElement) => {
    if (currentMdast == null) {
        // Create new mdast node
        if (newMdast) {
            if (isNonEmptyContent(newMdast)) {
                return (listeners.onBlockCreate &&
                    listeners.onBlockCreate(parsedText, newMdast, topLevelElementIndex));
            }
        }
    }
    else if (newMdast) {
        // The current mdast is existing and it's updated.
        if (isNonEmptyContent(newMdast)) {
            if (isNonEmptyContent(currentMdast)) {
                return (listeners.onBlockUpdate &&
                    listeners.onBlockUpdate(parsedText, newMdast, topLevelElementIndex, currentMdast, oldElement));
            }
            else {
                return (listeners.onBlockCreate &&
                    listeners.onBlockCreate(parsedText, newMdast, topLevelElementIndex));
            }
        }
        else {
            if (isNonEmptyContent(currentMdast)) {
                listeners.onBlockDelete &&
                    listeners.onBlockDelete(currentMdast, oldElement);
            }
        }
    }
    else {
        // The current mdast is existing and it's deleted.
        if (isNonEmptyContent(currentMdast)) {
            listeners.onBlockDelete &&
                listeners.onBlockDelete(currentMdast, oldElement);
        }
    }
};
const normalizeTopLevelElementWithSiblings = (editor, listeners, index, element, prevFamily, nextFamily) => {
    const idxOffset = index - prevFamily.length;
    const parsedElements = prevFamily.concat([element]).concat(nextFamily);
    const pseudoWrapper = {
        type: 'root',
        children: parsedElements,
    };
    // Parse again with siblings
    const blockTextsWithSibs = createBlockTexts(pseudoWrapper);
    const textToParseWithSibs = blockTextsWithSibs.entireText;
    const rootWithSibs = parse(textToParseWithSibs);
    const flattenedLeafBlocksWithSibs = flattenMdast(rootWithSibs);
    Editor.withoutNormalizing(editor, () => {
        // XXX: reverse() is mutable. Be careful!
        flattenedLeafBlocksWithSibs.reverse().forEach((leafBlock) => {
            if (leafBlock.self.position == null)
                return;
            const leafBlockPosition = leafBlock.self.position;
            const startLinePath = mapLine(blockTextsWithSibs, leafBlockPosition.start.line - 1);
            const endLinePath = mapLine(blockTextsWithSibs, leafBlockPosition.end.line - 1);
            if (startLinePath == null || endLinePath == null) {
                throw new Error('Failed to map lines');
            }
            const startTopLevelIdx = startLinePath[0];
            const endTopLevelIdx = endLinePath[0];
            if (startLinePath[0] < endLinePath[0]) {
                // Merge
                for (let i = endTopLevelIdx; i > startTopLevelIdx; --i) {
                    const srcIdx = i + idxOffset;
                    const dstIdx = srcIdx - 1;
                    const dstCurrentLines = editor.children[dstIdx]
                        .children.length;
                    Transforms.moveNodes(editor, {
                        at: [srcIdx],
                        match: (node) => node.type === 'line',
                        to: [dstIdx, dstCurrentLines],
                    });
                    Transforms.removeNodes(editor, { at: [srcIdx] });
                    endLinePath[0]--;
                    endLinePath[1] += dstCurrentLines;
                }
            }
            if (startLinePath[0] !== endLinePath[0]) {
                throw new Error('Assertion failed. startLinePath[0] !== endLinePath[0]');
            }
            const endLineIdx = endLinePath[0] + idxOffset;
            let setTargetIdx = endLineIdx;
            let prevSibCreated = false;
            if (endLinePath[1] !==
                editor.children[endLineIdx].children.length - 1) {
                // Split
                const idx = endLinePath[0] + idxOffset;
                Transforms.splitNodes(editor, { at: [idx, endLinePath[1]] });
                const sanitizeIdx = idx + 1;
                sanitizeTopLevelElement(editor, sanitizeIdx);
            }
            if (startLinePath[1] !== 0) {
                // Split
                const idx = startLinePath[0] + idxOffset;
                Transforms.splitNodes(editor, { at: [idx, startLinePath[1]] });
                const sanitizeIdx = idx + 1;
                sanitizeTopLevelElement(editor, sanitizeIdx);
                prevSibCreated = true;
                setTargetIdx++;
            }
            const setTargetElement = editor.children[setTargetIdx];
            const newProps = {
                type: leafBlock.self.type,
                mdastNode: leafBlock.self.type === 'empty' ||
                    leafBlock.self.type === 'placeholder'
                    ? undefined
                    : leafBlock.self,
                parentMdastNode: leafBlock.parent,
                flattenedMdastPath: leafBlock.path,
                flattenedMdastAncestors: leafBlock.ancestors,
            };
            const changeKind = checkTopLevelElementChanged(setTargetElement, newProps);
            if (changeKind === 'unchanged') {
                // Normalization finishes!
                return;
            }
            let additionalValue;
            if (changeKind !== 'parent-mdast-node-changed') {
                // If prevSibCreated, trying to call listeners is skipped in this iteration
                // since the equivalent procedure will be done in the next iteration.
                if (!prevSibCreated) {
                    const currentMdast = setTargetElement.mdastNode;
                    const newMdast = leafBlock.self;
                    const nodePosition = newMdast === null || newMdast === void 0 ? void 0 : newMdast.position;
                    const startOffset = nodePosition === null || nodePosition === void 0 ? void 0 : nodePosition.start.offset;
                    const endOffset = nodePosition === null || nodePosition === void 0 ? void 0 : nodePosition.end.offset;
                    const textOfThisElement = startOffset != null && endOffset != null
                        ? textToParseWithSibs.slice(startOffset, endOffset)
                        : '';
                    additionalValue = callListeners(listeners, currentMdast, newMdast, textOfThisElement, setTargetIdx, setTargetElement);
                }
            }
            Transforms.setNodes(editor, { ...additionalValue, ...newProps }, { at: [setTargetIdx] });
            if (isEmptyLikeContent(leafBlock.self)) {
                sanitizeTopLevelElement(editor, setTargetIdx);
            }
        });
    });
};
const NormalizeTopLevelElement = (listeners) => {
    const normalizeTopLevelElement = (element, index, editor) => {
        // NOTE: In normalizeTopLevelElement, it parse the content of paragraph-like blocks which has been just changed now
        //       and normalizes the block structures based on the parsing result.
        const path = [index];
        const blockTexts = createBlockTexts(element);
        // TODO: normalizeNodeから呼ぶと、最低2回parseが走る。回避したい。
        // -> 1. memoize(parse)
        // -> 2. setNodesしたpropを覚えておいて、2回目は即return
        const parsedText = blockTexts.entireText;
        const root = parse(parsedText);
        const childrenNum = root.children.length;
        if (childrenNum === 0) {
            if (element.flattenedMdastAncestors) {
                const [prevFound, prevFamily] = getUntilNonEmtpyElements(editor, index, true);
                const [nextFound, nextFamily] = getUntilNonEmtpyElements(editor, index, false);
                if (prevFound || nextFound) {
                    normalizeTopLevelElementWithSiblings(editor, listeners, index, element, prevFamily, nextFamily);
                    return;
                }
            }
            // Empty block
            if (element.type !== 'empty') {
                const newProps = {
                    type: 'empty',
                    flattenedMdastPath: [0],
                    flattenedMdastAncestors: ['root'],
                    mdastNode: undefined,
                    parentMdastNode: undefined,
                };
                const currentMdast = element.mdastNode;
                // empty element does not accept additional fields.
                callListeners(listeners, currentMdast, undefined, parsedText, index, element);
                Transforms.setNodes(editor, newProps, { at: path });
                sanitizeTopLevelElement(editor, index);
                // Switching non-empty to empty can break neighbor elements and they should be normalized if they were siblings of the emptified element.
                const preType = element.type;
                const preAncestors = element.flattenedMdastAncestors;
                if (preAncestors) {
                    if (index > 0) {
                        const prevIdx = index - 1;
                        const prevSib = editor.children[prevIdx];
                        if (isTopLevelSibling(prevSib, preType, preAncestors, true)) {
                            normalizeTopLevelElement(prevSib, prevIdx, editor);
                        }
                    }
                    if (index < editor.children.length - 1) {
                        const nextIdx = index + 1;
                        const nextSib = editor.children[index + 1];
                        if (element.flattenedMdastAncestors &&
                            isTopLevelSibling(nextSib, preType, preAncestors, false)) {
                            normalizeTopLevelElement(nextSib, nextIdx, editor);
                        }
                    }
                }
            }
            return;
        }
        const flattenedLeafBlocks = flattenMdast(root);
        if (flattenedLeafBlocks.length === 1) {
            const theFlattenedLeafBlock = flattenedLeafBlocks[0];
            // This is an exception.
            // For example, if the input is like following, the line of `line1` is parsed as `code` (indented code block) when parsed as a single line.
            // However, it is actually a part of a list.
            //
            // * line0
            //     * line1
            //
            // TODO: Make clean this code
            const maybeTheBlockIsList = theFlattenedLeafBlock.self.type === 'paragraph' ||
                theFlattenedLeafBlock.self.type === 'placeholder' ||
                theFlattenedLeafBlock.self.type === 'code';
            const ancestors = theFlattenedLeafBlock.ancestors;
            if (!maybeTheBlockIsList &&
                (theFlattenedLeafBlock.self.type === 'empty' || ancestors.length <= 1) // ancestors.length[0] must be 'root'
            ) {
                // The block has no ancestors. It's simple. Just update it based on the parsed data.
                // const newProps: Omit<TopLevelElement, 'children'> = {
                const newProps = {
                    type: theFlattenedLeafBlock.self.type,
                    mdastNode: theFlattenedLeafBlock.self.type === 'empty' ||
                        theFlattenedLeafBlock.self.type === 'placeholder'
                        ? undefined
                        : theFlattenedLeafBlock.self,
                    parentMdastNode: theFlattenedLeafBlock.parent,
                    flattenedMdastPath: theFlattenedLeafBlock.path,
                    flattenedMdastAncestors: theFlattenedLeafBlock.ancestors,
                };
                const changeKind = checkTopLevelElementChanged(element, newProps);
                if (changeKind === 'unchanged') {
                    // Normalization finishes!
                    return;
                }
                let additionalValue;
                if (changeKind !== 'parent-mdast-node-changed') {
                    const currentMdast = element.mdastNode;
                    const newMdast = theFlattenedLeafBlock.self;
                    additionalValue = callListeners(listeners, currentMdast, newMdast, parsedText, index, element);
                }
                Transforms.setNodes(editor, { ...additionalValue, ...newProps }, { at: path });
                if (isEmptyLikeContent(theFlattenedLeafBlock.self)) {
                    sanitizeTopLevelElement(editor, index);
                }
                return;
            }
        }
        const prevFamily = getToplevelSiblingElements(flattenedLeafBlocks[0].self, flattenedLeafBlocks[0].ancestors, index, editor, true);
        const nextFamily = getToplevelSiblingElements(flattenedLeafBlocks[flattenedLeafBlocks.length - 1].self, flattenedLeafBlocks[flattenedLeafBlocks.length - 1].ancestors, index, editor, false);
        normalizeTopLevelElementWithSiblings(editor, listeners, index, element, prevFamily, nextFamily);
    };
    return normalizeTopLevelElement;
};
export default NormalizeTopLevelElement;
//# sourceMappingURL=normalizeTopLevelElement.js.map